// Shared finite vocabulary: never send element text, form values, destinations,
// character names, or arbitrary data attributes to the analytics endpoint.
export const LANDING_CLICK_LIMIT = 24
export const LANDING_CLICK_BATCH = 8
export const LANDING_CLICK_SECTIONS = [
  'header',
  'hero',
  'browse',
  'features',
  'details',
  'companions',
  'faq',
  'closing',
  'footer',
  'landing',
]
export const LANDING_CLICK_ACTIONS = [
  'signup',
  'hero_artwork_signup',
  'login',
  'create',
  'explore',
  'discover',
  'chat',
  'guide',
  'gallery_card',
  'character_card',
  'feature_images',
  'feature_tweak',
  'feature_video',
  'feature_roleplay',
  'feature_photos',
  'feature_characters',
  'feature_styles',
  'feature_voice',
  'tab_images',
  'tab_tweak',
  'tab_video',
  'tab_roleplay',
  'tab_photos',
  'faq_open',
  'motion_toggle',
  ...['gallery_card', 'character_card'].flatMap((action) =>
    Array.from({ length: 24 }, (_, index) => `${action}_${index + 1}`),
  ),
]

export function normalizeLandingClick(value) {
  if (
    !value ||
    !LANDING_CLICK_SECTIONS.includes(value.section) ||
    !LANDING_CLICK_ACTIONS.includes(value.action) ||
    !/^[a-f0-9-]{36}$/.test(value.event_id) ||
    !Number.isSafeInteger(value.occurred_at) ||
    value.occurred_at < 0
  )
    return null
  return {
    event_id: value.event_id,
    section: value.section,
    action: value.action,
    occurred_at: value.occurred_at,
  }
}

export function landingClickTarget(target, origin) {
  const element = target?.closest?.(
    '[data-landing-action], a[href], button, [role="tab"], summary',
  )
  if (!element) return null
  const area = element.closest('[data-landing-section]')?.dataset.landingSection
  const inLanding = element.closest('[data-landing-variant]')
  const section = LANDING_CLICK_SECTIONS.includes(area)
    ? area
    : element.closest('header') && !inLanding
      ? 'header'
      : element.closest('footer')
        ? 'footer'
        : inLanding
          ? 'landing'
          : null
  if (!section) return null
  // Shared cards own their links/buttons; the landing wrapper owns attribution.
  const explicitTarget = element.closest('[data-landing-action]')
  let action = explicitTarget?.dataset.landingAction
  if (action === 'faq_open' && element.closest('details')?.open) return null
  if (!LANDING_CLICK_ACTIONS.includes(action)) {
    const anchor = element.closest('a[href]')
    if (anchor) {
      let url
      try {
        url = new URL(anchor.href, origin)
      } catch {
        return null
      }
      if (url.origin !== origin) return null
      const path = url.pathname.replace(/\/+$/, '') || '/'
      action = {
        '/signup':
          url.searchParams.get('mode') === 'login' ? 'login' : 'signup',
        '/generate': 'create',
        '/explore': 'explore',
        '/chat': 'chat',
      }[path]
      if (path === '/articles' || path.startsWith('/articles/'))
        action = 'guide'
    } else if (element.tagName === 'SUMMARY' && !element.parentElement?.open)
      action = 'faq_open'
  }
  const item = explicitTarget?.dataset.landingItem
  if (
    ['gallery_card', 'character_card'].includes(action) &&
    /^(?:[1-9]|1\d|2[0-4])$/.test(item)
  )
    action = `${action}_${item}`
  return LANDING_CLICK_ACTIONS.includes(action) ? { section, action } : null
}

export const LANDING_EXPOSURE_LOCALES = [
  'en',
  'de',
  'es',
  'fr',
  'hi',
  'it',
  'ja',
  'ko',
  'pt',
  'zh-CN',
]
export const LANDING_EXPOSURE_DEVICES = ['mobile', 'tablet', 'desktop']

// Exposure is deliberately bounded: no browser fingerprint, screen dimensions,
// freeform section IDs, or arbitrary language strings leave the browser.
export function normalizeLandingExposure(value = {}) {
  const context = {}
  if (LANDING_EXPOSURE_LOCALES.includes(value.context?.locale))
    context.locale = value.context.locale
  if (
    [...LANDING_EXPOSURE_LOCALES, 'other'].includes(
      value.context?.browser_locale,
    )
  )
    context.browser_locale = value.context.browser_locale
  if (LANDING_EXPOSURE_DEVICES.includes(value.context?.device))
    context.device = value.context.device
  const sections = [
    ...new Set(
      (Array.isArray(value.sections) ? value.sections : []).filter((section) =>
        LANDING_CLICK_SECTIONS.includes(section),
      ),
    ),
  ].slice(0, LANDING_CLICK_SECTIONS.length)
  return {
    ...(Object.keys(context).length ? { context } : {}),
    ...(sections.length ? { sections } : {}),
  }
}

export function normalizeLandingBrowserLocale(value) {
  if (typeof value !== 'string') return 'other'
  const lower = value.toLowerCase()
  if (lower === 'zh' || lower.startsWith('zh-')) return 'zh-CN'
  const base = lower.split('-')[0]
  return LANDING_EXPOSURE_LOCALES.includes(base) ? base : 'other'
}
